// client/src/components/notaFiscal/NotaFiscalPage.js
import React, { Fragment, useState } from 'react';
import { Receipt, Loader2, RefreshCw, RotateCw, ExternalLink } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import {
    useNotasFiscais,
    useReemitirNotaFiscal,
    useConsultarStatusNotaFiscal,
} from '../../hooks/useNotaFiscal';

const STATUS_LABEL = { pendente: 'Pendente', processando: 'Processando', emitida: 'Emitida', erro: 'Erro', cancelada: 'Cancelada' };
const STATUS_STYLE = {
    pendente: { background: '#f1f5f9', color: '#475569' },
    processando: { background: '#fef3c7', color: '#92400e' },
    emitida: { background: '#dcfce7', color: '#166534' },
    erro: { background: '#fee2e2', color: '#991b1b' },
    cancelada: { background: '#f3f4f6', color: '#6b7280' },
};

const StatusBadge = ({ status }) => (
    <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600, ...(STATUS_STYLE[status] || STATUS_STYLE.pendente) }}>
        {STATUS_LABEL[status] || status}
    </span>
);

const formatValor = (v) => `R$ ${Number(v || 0).toFixed(2).replace('.', ',')}`;

const NotaFiscalPage = () => {
    const [statusFiltro, setStatusFiltro] = useState('');

    const { data, isLoading, isError, error, isFetching } = useNotasFiscais({ status: statusFiltro || undefined, limit: 50 });
    const reemitir = useReemitirNotaFiscal();
    const consultarStatus = useConsultarStatusNotaFiscal();

    const notas = data?.data || [];

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<Receipt />}
                    title="Notas Fiscais"
                    subtitle={!isLoading ? `${data?.total ?? notas.length} nota${(data?.total ?? notas.length) !== 1 ? 's' : ''}` : null}
                />

                <div style={{ display: 'flex', gap: 8, marginBottom: '1rem', flexWrap: 'wrap' }}>
                    {['', 'pendente', 'processando', 'emitida', 'erro', 'cancelada'].map((s) => (
                        <button
                            key={s || 'todos'}
                            onClick={() => setStatusFiltro(s)}
                            className={`action-button ${statusFiltro === s ? 'action-button-primary' : 'action-button-secondary'}`}
                            style={{ fontSize: '0.8125rem' }}
                        >
                            {s ? STATUS_LABEL[s] : 'Todos'}
                        </button>
                    ))}
                </div>
            </div>

            <div className="table-container">
                <div className="table-wrapper">
                    {isLoading ? (
                        <div className="table-loading-state"><Loader2 size={24} className="animate-spin" /><span>Carregando notas fiscais...</span></div>
                    ) : isError ? (
                        <div className="table-empty-state">
                            <h3>Não foi possível carregar</h3>
                            <p>{error?.response?.data?.error || 'Erro ao carregar as notas fiscais.'}</p>
                        </div>
                    ) : notas.length > 0 ? (
                        <table className="styled-table">
                            <thead>
                                <tr>
                                    <th>Data</th>
                                    <th>Tipo</th>
                                    <th>Cliente</th>
                                    <th>Valor</th>
                                    <th>Número</th>
                                    <th>Status</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {notas.map((nota) => (
                                    <tr key={nota._id}>
                                        <td>{new Date(nota.createdAt).toLocaleDateString('pt-BR')}</td>
                                        <td style={{ textTransform: 'uppercase' }}>{nota.tipo}</td>
                                        <td>{nota.cliente?.nome || '—'}</td>
                                        <td>{formatValor(nota.valorTotal)}</td>
                                        <td>{nota.numero || '—'}</td>
                                        <td><StatusBadge status={nota.status} /></td>
                                        <td className="text-right">
                                            <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                                                {nota.status === 'processando' && (
                                                    <button
                                                        onClick={() => consultarStatus.mutate(nota._id)}
                                                        className="action-button-icon text-blue-600"
                                                        title="Consultar status junto ao provedor"
                                                        disabled={consultarStatus.isPending}
                                                    >
                                                        <RefreshCw size={18} className={consultarStatus.isPending && consultarStatus.variables === nota._id ? 'animate-spin' : ''} />
                                                    </button>
                                                )}
                                                {nota.status === 'erro' && (
                                                    <button
                                                        onClick={() => reemitir.mutate(nota._id)}
                                                        className="action-button-icon text-amber-600"
                                                        title="Reemitir"
                                                        disabled={reemitir.isPending}
                                                    >
                                                        <RotateCw size={18} className={reemitir.isPending && reemitir.variables === nota._id ? 'animate-spin' : ''} />
                                                    </button>
                                                )}
                                                {nota.urlPdf && (
                                                    <a href={nota.urlPdf} target="_blank" rel="noopener noreferrer" className="action-button-icon text-gray-600" title="Ver PDF">
                                                        <ExternalLink size={18} />
                                                    </a>
                                                )}
                                            </div>
                                            {nota.status === 'erro' && nota.erro && (
                                                <p style={{ fontSize: '0.75rem', color: '#991b1b', marginTop: 4, textAlign: 'right' }}>{nota.erro}</p>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    ) : (
                        <div className="table-empty-state">
                            <Receipt size={48} />
                            <h3>Nenhuma nota fiscal encontrada</h3>
                            <p>{statusFiltro ? 'Nenhuma nota nesse status.' : 'Notas fiscais aparecem aqui automaticamente quando uma venda é paga (se a emissão automática estiver ligada em Configurações → Nota Fiscal).'}</p>
                        </div>
                    )}
                    {isFetching && !isLoading && (
                        <div style={{ padding: '0.5rem 1rem', fontSize: '0.75rem', color: 'var(--color-text-secondary)' }}>Atualizando...</div>
                    )}
                </div>
            </div>
        </Fragment>
    );
};

export default NotaFiscalPage;
